/**
 * SEO-friendly listing URLs: /search/{categorySlug} instead of ?category={id}.
 * Filters (q, page, price, etc.) stay as query params on that path.
 */

import { readVerifiedOnlySession } from '@/lib/verifiedOnlySession';

export type ListingFilters = {
  q?: string;
  /** Category slug (or legacy numeric id — prefer slug in hrefs). */
  category?: string;
  page?: string;
  min_price?: string;
  max_price?: string;
  location?: string;
  verified_only?: string;
  sort?: string;
  radius?: string;
  conditions?: string[];
  brands?: string[];
  min_rating?: string;
  /** `stores` = online store products only; `classifieds` = individual seller ads only */
  listing_type?: string;
  vehicle_brand?: string;
  vehicle_model?: string;
  year_min?: string;
  year_max?: string;
  mileage_min?: string;
  mileage_max?: string;
  engine_cc_min?: string;
  engine_cc_max?: string;
  fuel_type?: string[];
  transmission?: string[];
};

export function buildListingHref(filters: ListingFilters): string {
  const base = filters.category ? `/search/${encodeURIComponent(filters.category)}` : '/search';
  const sp = new URLSearchParams();
  if (filters.q) sp.set('q', filters.q);
  if (filters.page && filters.page !== '1') sp.set('page', filters.page);
  if (filters.min_price) sp.set('min_price', filters.min_price);
  if (filters.max_price) sp.set('max_price', filters.max_price);
  if (filters.location) sp.set('location', filters.location);
  let verifiedOnly = filters.verified_only;
  if ((verifiedOnly === undefined || verifiedOnly === '') && typeof window !== 'undefined') {
    const session = readVerifiedOnlySession();
    if (session) verifiedOnly = session;
  }
  if (verifiedOnly !== undefined && verifiedOnly !== '') {
    sp.set('verified_only', verifiedOnly === '1' ? '1' : '0');
  }
  if (filters.sort) sp.set('sort', filters.sort);
  if (filters.radius) sp.set('radius', filters.radius);
  (filters.conditions ?? []).forEach((c) => sp.append('conditions[]', c));
  (filters.brands ?? []).forEach((b) => sp.append('brands[]', b));
  if (filters.min_rating) sp.set('min_rating', filters.min_rating);
  if (filters.listing_type) sp.set('listing_type', filters.listing_type);
  if (filters.vehicle_brand) sp.set('vehicle_brand', filters.vehicle_brand);
  if (filters.vehicle_model) sp.set('vehicle_model', filters.vehicle_model);
  if (filters.year_min) sp.set('year_min', filters.year_min);
  if (filters.year_max) sp.set('year_max', filters.year_max);
  if (filters.mileage_min) sp.set('mileage_min', filters.mileage_min);
  if (filters.mileage_max) sp.set('mileage_max', filters.mileage_max);
  if (filters.engine_cc_min) sp.set('engine_cc_min', filters.engine_cc_min);
  if (filters.engine_cc_max) sp.set('engine_cc_max', filters.engine_cc_max);
  (filters.fuel_type ?? []).forEach((f) => sp.append('fuel_type[]', f));
  (filters.transmission ?? []).forEach((t) => sp.append('transmission[]', t));
  const qs = sp.toString();
  return qs ? `${base}?${qs}` : base;
}

/** Append `verified_only=1` to bare `/search` paths when the session filter is on (client-only). */
export function searchPathWithVerifiedOnly(path: string, explicit?: string): string {
  const vo =
    explicit === '1'
      ? '1'
      : explicit === '0'
        ? '0'
        : typeof window !== 'undefined'
          ? readVerifiedOnlySession()
          : null;
  if (vo !== '1') return path;
  const qIndex = path.indexOf('?');
  const base = qIndex >= 0 ? path.slice(0, qIndex) : path;
  const sp = new URLSearchParams(qIndex >= 0 ? path.slice(qIndex + 1) : '');
  sp.set('verified_only', vo === '1' ? '1' : '0');
  const qs = sp.toString();
  return qs ? `${base}?${qs}` : base;
}

/** True if this category filter matches the row (supports slug or legacy id). */
export function categoryFilterMatches(filtersCategory: string | undefined, cat: { id: number; slug: string }): boolean {
  if (filtersCategory == null || filtersCategory === '') return false;
  return filtersCategory === cat.slug || filtersCategory === String(cat.id);
}

/** `/search/{slug}` from API category name (same idea as Header nav: canonical slug from Laravel). */
export function searchHrefForCategoryName(
  categories: { name?: string | null; slug?: string | null }[],
  matchName: string,
  fallbackSlug: string,
): string {
  const needle = matchName.trim().toLowerCase();
  const found = categories.find((c) => String(c.name ?? '').trim().toLowerCase() === needle);
  const slug = (found?.slug && String(found.slug).trim()) || fallbackSlug;
  return `/search/${encodeURIComponent(slug)}`;
}
